import { t } from "i18next";
import { z } from "zod";

import {
  buildTranslationsIndexes,
  compareTranslationsByDefaultThenLanguage,
} from "./common";
import languageCodeList from "../../constants/languages";

import type { Language, LanguageCode } from "../../api/ProjectAPI.types";
import type {
  AdvertisingTranslation,
  IAdvertisingSingle,
  Translation,
} from "../../types";

const URL_MAX_LENGTH = 128;

export function getAdvertisingTranslationSchema() {
  return z.object({
    language: z.enum(languageCodeList),
    language_uuid: z.string().optional(),
    default: z.boolean(),
    uuid: z.string().optional(),
    url: z.string().max(
      URL_MAX_LENGTH,
      t("form:maxLength", {
        field: t("form:fields.url"),
        max: URL_MAX_LENGTH,
      }),
    ),
  });
}

export function getAdvertisingTranslationsFormSchema() {
  return z.object({
    translations: z.array(getAdvertisingTranslationSchema()),
  });
}

export interface AdvertisingTranslationFormField {
  language: LanguageCode;
  language_uuid?: string;
  default: boolean;
  uuid?: string;
  url: string;
}

/** The video of one language, in the shape the campaign payload expects. */
export interface AdvertisingVideoFields {
  filename: string;
  video_url: string;
}

export type AdvertisingVideoByLanguageUuid = Record<
  string,
  AdvertisingVideoFields
>;

const NO_VIDEO: AdvertisingVideoFields = { filename: "", video_url: "" };

type AdvertisingTranslationToCreate = Omit<AdvertisingTranslation, "uuid"> & {
  uuid?: string;
};

function findLanguageCode(
  languageList: Language[],
  languageUuid: string,
): LanguageCode | undefined {
  const matchingLanguage = languageList.find(
    (language) => language.uuid === languageUuid,
  );

  return languageCodeList.find(
    (languageCode) => languageCode === matchingLanguage?.language,
  );
}

export function apiToFormTranslations(
  translations: AdvertisingTranslation[],
  languageList: Language[],
): AdvertisingTranslationFormField[] {
  return translations
    .flatMap((translation) => {
      const language = findLanguageCode(
        languageList,
        translation.language_uuid,
      );

      if (!language) {
        console.error(
          `Unknown language_uuid "${translation.language_uuid}" on advertising translation "${translation.uuid}", translation ignored.`,
        );

        return [];
      }

      return [
        {
          language,
          language_uuid: translation.language_uuid,
          default: translation.default,
          uuid: translation.uuid,
          url: translation.url ?? "",
        },
      ];
    })
    .sort(compareTranslationsByDefaultThenLanguage);
}

/**
 * Indexes the server side videos by language_uuid. The video is server data:
 * it never enters the form, so every consumer resolves it from here.
 */
export function apiToVideoByLanguageUuid(
  translations: AdvertisingTranslation[],
): AdvertisingVideoByLanguageUuid {
  return Object.fromEntries(
    translations.map((translation) => [
      translation.language_uuid,
      {
        filename: translation.filename ?? "",
        video_url: translation.video_url ?? "",
      },
    ]),
  );
}

/**
 * Adapts the `createAdvertisingTranslation` response into the
 * AdvertisingTranslationFormField shape expected by the form, so the newly
 * added language can be appended to the field array.
 */
export function createTranslationResponseToFormField(
  response: Translation,
  language: LanguageCode,
): AdvertisingTranslationFormField {
  return {
    uuid: response.uuid,
    default: response.default,
    language_uuid: response.language_uuid,
    url: response.url ?? "",
    language,
  };
}

/**
 * Converts form field values back to the campaign payload shape.
 * `language` is intentionally left out: the advertising API only knows
 * language_uuid.
 *
 * The video fields are read from `videoByLanguageUuid` rather than dropped:
 * whether the backend preserves fields absent from the payload has never been
 * verified, and being wrong about it would erase every video on the first save.
 */
export function formToApiTranslations(
  formTranslations: AdvertisingTranslationFormField[],
  videoByLanguageUuid: AdvertisingVideoByLanguageUuid,
): Pick<IAdvertisingSingle, "translations" | "translations_indexes"> {
  const translations: AdvertisingTranslationToCreate[] = formTranslations.map(
    (field) => ({
      uuid: field.uuid,
      default: field.default,
      language_uuid: field.language_uuid ?? "",
      url: field.url,
      ...(videoByLanguageUuid[field.language_uuid ?? ""] ?? NO_VIDEO),
    }),
  );

  return {
    // Translation requires a uuid, which a not yet created translation cannot
    // have. This is the single place that optionality crosses into the API type.
    translations: translations as IAdvertisingSingle["translations"],
    translations_indexes: buildTranslationsIndexes(formTranslations),
  };
}
